import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { api } from '../api';

/**
 * Limites do plano gratuito.
 *
 * Estes números são regra de negócio, não constante de código: mudam conforme a
 * gente aprende o que converte. Por isso vivem em tabela e se ajustam por aqui,
 * sem deploy - e quem decide o bloqueio continua sendo o backend.
 */
interface Formulario {
  freeWeeklyAnswers: number;
  freeWeeklyCommentaries: number;
  freeWeeklyLibraryReads: number;
  dailyCommentaryCap: number;
  dailyLibraryCap: number;
  scrapeAlertThreshold: number;
  inactivityEmailEnabled: boolean;
  inactivityEmailDelayHours: number;
  /** Lista vazia é sem restrição de prova: o catálogo inteiro, dentro da cota. */
  freeExamIds: string[];
}

export function AcessoPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Formulario>();

  const configuracao = useQuery({
    queryKey: ['access-settings'],
    queryFn: () => api.getAccessSettings(),
  });

  useEffect(() => {
    if (!configuracao.data) return;
    form.setFieldsValue({
      freeWeeklyAnswers: configuracao.data.freeWeeklyAnswers,
      freeWeeklyCommentaries: configuracao.data.freeWeeklyCommentaries,
      freeWeeklyLibraryReads: configuracao.data.freeWeeklyLibraryReads,
      dailyCommentaryCap: configuracao.data.dailyCommentaryCap,
      dailyLibraryCap: configuracao.data.dailyLibraryCap,
      scrapeAlertThreshold: configuracao.data.scrapeAlertThreshold,
      inactivityEmailEnabled: configuracao.data.inactivityEmailEnabled,
      inactivityEmailDelayHours: configuracao.data.inactivityEmailDelayHours,
      freeExamIds: configuracao.data.freeExamIds,
    });
  }, [configuracao.data, form]);

  /*
   * As provas publicadas, para escolher quais o gratuito alcança.
   *
   * Só as publicadas: apontar a degustação para uma prova em rascunho a
   * deixaria vazia, e o aluno gratuito não veria questão nenhuma - um jeito
   * silencioso de desligar o plano gratuito inteiro.
   *
   * **`pageSize` é 100 porque 100 é o teto da rota**, e pedir 200 devolvia
   * **422**. O efeito era mudo e total: a lista nunca chegava, o `Select`
   * mostrava "Não há dados" e a escolha da prova gratuita, entregue em
   * 13/09/2026, nunca pôde ser feita. O `free_exam_id` estava nulo em produção
   * por isso, e não porque ninguém tivesse decidido.
   *
   * É a terceira vez que o teto de `pageSize` morde deste mesmo jeito: nada
   * falha no lint, nos tipos nem no build, porque o limite mora no Zod da API,
   * do outro lado da rede.
   */
  const provas = useQuery({
    queryKey: ['exams', 'published'],
    queryFn: () => api.listExams({ status: 'published', pageSize: 100 }),
  });

  /*
   * Uma prova escolhida que saiu do catálogo publicado (despublicada ou
   * apagada) continua na lista do banco e não casa com questão nenhuma. Sem
   * esta linha o `Select` a mostraria como um uuid cru; com ela, o Dono vê o
   * que é e decide tirar.
   */
  const publicadas = provas.data?.items ?? [];
  const foraDoCatalogo = provas.data
    ? (configuracao.data?.freeExamIds ?? []).filter(
        (id) => !publicadas.some((prova) => prova.id === id),
      )
    : [];
  const opcoesDeProva = [
    ...publicadas.map((prova) => ({ value: prova.id, label: prova.name })),
    ...foraDoCatalogo.map((id) => ({ value: id, label: 'Prova fora do catálogo publicado' })),
  ];

  const salvar = useMutation({
    mutationFn: (valores: Formulario) =>
      /*
       * O `Select` limpo com o "x" devolve `undefined`, e não lista vazia: sem
       * isto o campo some do corpo do PATCH, que é parcial, e o servidor
       * entenderia "não mexa" onde o Dono quis dizer "tire a restrição".
       */
      api.updateAccessSettings({ ...valores, freeExamIds: valores.freeExamIds ?? [] }),
    onSuccess: async () => {
      message.success('Configuração atualizada.');
      await queryClient.invalidateQueries({ queryKey: ['access-settings'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Acesso e comunicação
        </Typography.Title>
        <Typography.Text type="secondary">
          Convite para começar a estudar, cotas do plano gratuito e tetos de quem assina.
        </Typography.Text>
      </div>

      {configuracao.error ? (
        <Alert type="error" showIcon title={(configuracao.error as Error).message} />
      ) : null}

      <Card size="small" loading={configuracao.isPending} style={{ maxWidth: 640 }}>
        <Form<Formulario> form={form} layout="vertical" onFinish={(v) => salvar.mutate(v)}>
          <Typography.Title level={5}>Convite para a primeira questão</Typography.Title>
          <Typography.Paragraph type="secondary">
            Envia uma única mensagem a alunos com e-mail confirmado que ainda não responderam
            nenhuma questão. O prazo conta desde a criação da conta. Ao ativar, contas antigas
            elegíveis também entram. A verificação acontece a cada hora.
          </Typography.Paragraph>
          <Form.Item name="inactivityEmailEnabled" label="Enviar convite por e-mail" valuePropName="checked">
            <Switch checkedChildren="Ativado" unCheckedChildren="Desativado" />
          </Form.Item>
          <Form.Item name="inactivityEmailDelayHours" label="Horas após a criação da conta"
            rules={[{ required: true, message: 'Informe o prazo.' }, { type: 'integer', min: 1, max: 2160 }]}>
            <InputNumber min={1} max={2160} precision={0} addonAfter="horas" />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            Assunto: Seu primeiro comentário do Enac está aqui. O convite leva à área de questões
            e oferece descadastro. Alterar o prazo não reenvia mensagens já entregues.
          </Typography.Paragraph>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="freeWeeklyAnswers"
                label="Questões respondidas por semana"
                tooltip="Zero torna o módulo exclusivo de assinantes."
                rules={[{ required: true, message: 'Informe o limite.' }]}
              >
                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="freeWeeklyCommentaries"
                label="Resoluções abertas por semana"
                tooltip="A resolução é o principal valor da plataforma."
                rules={[{ required: true, message: 'Informe o limite.' }]}
              >
                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="freeWeeklyLibraryReads"
                label="Artigos comentados por semana"
                tooltip="Zero deixa a Legislação Comentada exclusiva de assinantes."
                rules={[{ required: true, message: 'Informe o limite.' }]}
              >
                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Até onde o gratuito chega
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            A cota acima limita o <strong>ritmo</strong>; esta escolha limita a{' '}
            <strong>amplitude</strong>. Sem prova escolhida, quem não paga passeia pelo catálogo
            inteiro à velocidade da cota, e a diferença para quem assina é só de velocidade. Com
            provas escolhidas, elas viram a amostra e as demais são o que se vende. Questão
            marcada como amostra grátis continua passando, esteja em que prova estiver.
          </Typography.Paragraph>

          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="freeExamIds"
                label="Provas liberadas ao plano gratuito"
                tooltip="Vazio devolve o catálogo inteiro ao gratuito, dentro da cota."
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={provas.isPending}
                  placeholder="Sem restrição: o catálogo inteiro, dentro da cota"
                  options={opcoesDeProva}
                />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Teto de quem assina
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Assinar não é ler sem limite: sem teto, o acervo inteiro sai em minutos pelo preço de
            uma assinatura. Os números abaixo são folgados para quem estuda - uma prova inteira
            são cem questões - e estreitos para um script. Zero desliga o teto.
          </Typography.Paragraph>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="dailyCommentaryCap"
                label="Resoluções por dia (assinante)"
                tooltip="Vale para quem tem o direito ativo. Reabrir a mesma questão no dia não conta de novo."
                rules={[{ required: true, message: 'Informe o teto.' }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="dailyLibraryCap"
                label="Artigos comentados por dia (assinante)"
                tooltip="Conta o comentário entregue, inclusive na leitura da lei inteira. Reabrir o mesmo artigo no dia não conta de novo."
                rules={[{ required: true, message: 'Informe o teto.' }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="scrapeAlertThreshold"
                label="Aviso de ritmo (por hora)"
                tooltip="Não barra ninguém: acima disso numa hora, a conta entra no aviso da ronda."
                rules={[{ required: true, message: 'Informe o limiar.' }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Space orientation="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              A semana do plano gratuito vira na segunda-feira, à meia-noite de Brasília; o teto
              de quem assina vira à meia-noite todo dia. Reabrir a mesma questão ou o mesmo artigo
              na mesma semana não gasta cota de novo.
            </Typography.Text>
            <Button type="primary" htmlType="submit" loading={salvar.isPending}>
              Salvar configuração
            </Button>
          </Space>
        </Form>
      </Card>
    </Space>
  );
}
